import { type ContactMethod, type OtpContactType, type UserProfileDataType } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { isAuthError } from '@supabase/supabase-js'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { deriveFeedbackContactMethod } from '~/components/feedback/feedbackContactHelpers'
import { type ContactForConsent } from '~/components/feedback/feedbackFlowTypes'
import { useAlert } from '~/components/GlobalAlert'
import { useAdoptResponder } from '~/data/feedback/useFeedback'
import { useSetupBuyerAccount } from '~/data/user/useUserProfile'
import { usePostHog } from '~/lib/analytics/posthog'
import { getOrCreateAnonymousUser } from '~/lib/auth/anonymous'
import { sendAuthContactOtp } from '~/lib/auth/contactOtp'
import { useChangeAuthContact, useResendOtp, useSendOtp, useVerifyOtp } from '~/lib/auth/useOtpAuth'
import { useOtpStateManager } from '~/lib/auth/useOtpStateManager'
import { trpc } from '~/lib/trpc'

type FeedbackConsentOtpMode = 'login' | 'update' | null

type FeedbackConsentOtpErrorSource = 'identifier-check' | 'send' | 'verify' | 'resend'

type FeedbackConsentErrorClassification =
  | 'user-fixable'
  | 'non-user-fixable'
  | 'service-unavailable'

type UseFeedbackConsentOtpOptions = {
  contact: ContactForConsent
  feedbackId?: string | null
  nonce?: string | null
  isFlowLoading: boolean
  onCompleteFlow: () => Promise<boolean> | Promise<void> | boolean | void
  setFlowIsLoading: (loading: boolean) => void
}

type UseFeedbackConsentOtpResult = {
  contactMethod: ContactMethod | null
  otp: string
  setOtp: (value: string) => void
  showOtpPhase: boolean
  isLoading: boolean
  isVerifying: boolean
  sendOtp: () => Promise<boolean>
  handleResendOtp: () => Promise<void>
  otpStateManager: ReturnType<typeof useOtpStateManager>
}

type HandleOtpErrorParams = {
  error: unknown
  source: FeedbackConsentOtpErrorSource
  contactType: OtpContactType
  showToUser: boolean
  kind: 'send' | 'verify'
}

function classifyOtpError(error: unknown): FeedbackConsentErrorClassification {
  if (isAuthError(error)) {
    const status = typeof error.status === 'number' ? error.status : null
    const code = typeof error.code === 'string' ? error.code : ''

    if (code === 'phone_exists' || code === 'email_exists') {
      return 'non-user-fixable'
    }

    if (status && status >= 500) {
      return 'service-unavailable'
    }

    if (code === 'otp_expired' || code === 'invalid_token') {
      return 'user-fixable'
    }

    if (code === 'over_request_rate_limit') {
      return 'user-fixable'
    }

    return 'non-user-fixable'
  }

  return 'service-unavailable'
}

export function useFeedbackConsentOtp({
  contact,
  feedbackId,
  nonce,
  isFlowLoading,
  onCompleteFlow,
  setFlowIsLoading,
}: UseFeedbackConsentOtpOptions): UseFeedbackConsentOtpResult {
  const { t } = useTranslations(['feedback', 'common'])
  const { showAlert } = useAlert()

  const otpStateManager = useOtpStateManager()
  const ensureBuyerSetup = useSetupBuyerAccount()
  const changeAuthContact = useChangeAuthContact()
  const resendOtpMutation = useResendOtp()
  const verifyOtpMutation = useVerifyOtp()
  const sendOtpLogin = useSendOtp({ allowAccountCreation: false })
  const adoptResponderMutation = useAdoptResponder()
  const checkIdentifierMutation = useMutation(trpc.users.checkIdentifier.mutationOptions())

  const posthog = usePostHog()

  const [otpMode, setOtpMode] = useState<FeedbackConsentOtpMode>(null)
  const [otp, _setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)

  const autoSubmitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (autoSubmitTimeoutRef.current) {
        clearTimeout(autoSubmitTimeoutRef.current)
      }
    },
    []
  )

  const contactMethod = useMemo(() => deriveFeedbackContactMethod(contact), [contact])

  const handleOtpError = useCallback(
    ({ error, source, contactType, showToUser, kind }: HandleOtpErrorParams) => {
      const classification = classifyOtpError(error)

      if (isAuthError(error) && (error.code === 'phone_exists' || error.code === 'email_exists')) {
        return
      }

      console.error('[FeedbackConsentOtp]', {
        source,
        contactType,
        classification,
        error,
      })

      _setOtp('')

      if (!showToUser) {
        return
      }

      if (classification === 'user-fixable' || classification === 'service-unavailable') {
        const description =
          kind === 'verify'
            ? t(($) => $.errors.otpVerificationFailed)
            : t(($) => $.errors.otpSendFailed)

        showAlert({
          type: 'error',
          title: t(($) => $.errors.title),
          description,
        })
      }
    },
    [showAlert, t]
  )

  const resolveOtpMode = useCallback(async () => {
    if (!contactMethod) {
      return null
    }

    try {
      const checkInput =
        contactMethod.type === 'email'
          ? { email: contactMethod.value }
          : { phone: contactMethod.value }

      const checkResult = await checkIdentifierMutation.mutateAsync(checkInput)

      const mode = checkResult.mode
      setOtpMode(mode)
      return mode
    } catch (error) {
      handleOtpError({
        error,
        source: 'identifier-check',
        contactType: contactMethod.type,
        showToUser: false,
        kind: 'send',
      })

      return null
    }
  }, [checkIdentifierMutation, contactMethod, handleOtpError])

  const isVerifying =
    verifyOtpMutation.isPending ||
    ensureBuyerSetup.isPending ||
    adoptResponderMutation.isPending ||
    isFlowLoading

  const isLoading =
    isVerifying ||
    changeAuthContact.isPending ||
    sendOtpLogin.isPending ||
    checkIdentifierMutation.isPending

  const sendOtp = useCallback(async () => {
    if (!contactMethod) {
      return false
    }

    if (isLoading) {
      return false
    }

    if (otpStateManager.hasRecentOtp(contactMethod.value)) {
      setOtpSent(true)
      return true
    }

    const resolvedMode = otpMode ?? (await resolveOtpMode())

    if (!resolvedMode) {
      return false
    }

    if (resolvedMode === 'login') {
      try {
        const sendProps =
          contactMethod.type === 'email'
            ? { email: contactMethod.value }
            : { phone: contactMethod.value }

        await sendOtpLogin.mutateAsync(sendProps)
        otpStateManager.markOtpSent(contactMethod.value)
        setOtpSent(true)
        return true
      } catch (error) {
        handleOtpError({
          error,
          source: 'send',
          contactType: contactMethod.type,
          showToUser: true,
          kind: 'send',
        })

        return false
      }
    }

    try {
      await getOrCreateAnonymousUser()

      const result = await sendAuthContactOtp({
        contactType: contactMethod.type,
        contactValue: contactMethod.value,
        changeAuthContact,
        otpStateManager,
        metadata: {
          name: contact.name,
          email: contact.email ?? undefined,
          phone_number: contact.phone ?? undefined,
        } satisfies UserProfileDataType,
        onError: (error) =>
          handleOtpError({
            error,
            source: 'send',
            contactType: contactMethod.type,
            showToUser: true,
            kind: 'send',
          }),
      })

      if (result) {
        setOtpSent(true)
      }

      return result
    } catch (error) {
      if (isAuthError(error) && (error.code === 'phone_exists' || error.code === 'email_exists')) {
        setOtpMode('login')

        try {
          const sendProps =
            contactMethod.type === 'email'
              ? { email: contactMethod.value }
              : { phone: contactMethod.value }

          await sendOtpLogin.mutateAsync(sendProps)
          otpStateManager.markOtpSent(contactMethod.value)
          setOtpSent(true)
          return true
        } catch (loginError) {
          handleOtpError({
            error: loginError,
            source: 'send',
            contactType: contactMethod.type,
            showToUser: true,
            kind: 'send',
          })

          return false
        }
      }

      handleOtpError({
        error,
        source: 'send',
        contactType: contactMethod.type,
        showToUser: true,
        kind: 'send',
      })

      return false
    }
  }, [
    changeAuthContact,
    contact,
    contactMethod,
    handleOtpError,
    otpMode,
    otpStateManager,
    resolveOtpMode,
    sendOtpLogin,
    isLoading,
  ])

  const handleVerifyOtp = useCallback(async () => {
    if (!contactMethod || otp.length !== 6) {
      return
    }

    setFlowIsLoading(true)

    try {
      let currentMode = otpMode

      if (!currentMode) {
        currentMode = await resolveOtpMode()

        if (!currentMode) {
          return
        }
      }

      const isLoginMode = currentMode === 'login'

      const verifyProps = isLoginMode
        ? contactMethod.type === 'email'
          ? { email: contactMethod.value, token: otp, type: 'email' as const }
          : { phone: contactMethod.value, token: otp, type: 'sms' as const }
        : contactMethod.type === 'email'
          ? {
              email: contactMethod.value,
              token: otp,
              type: 'email_change' as const,
            }
          : {
              phone: contactMethod.value,
              token: otp,
              type: 'phone_change' as const,
            }

      const user = await verifyOtpMutation.mutateAsync(verifyProps)

      if (!user) {
        handleOtpError({
          error: new Error('otp_verification_failed'),
          source: 'verify',
          contactType: contactMethod.type,
          showToUser: true,
          kind: 'verify',
        })

        return
      }

      const userProfileData = {
        name: contact.name,
        avatar_url: user.user_metadata.avatar_url || '',
      } satisfies UserProfileDataType

      const setupResult = await ensureBuyerSetup.mutateAsync({
        profileData: userProfileData,
      })

      if (!setupResult) {
        handleOtpError({
          error: new Error('buyer_profile_setup_failed'),
          source: 'verify',
          contactType: contactMethod.type,
          showToUser: true,
          kind: 'verify',
        })

        return
      }

      if (isLoginMode && feedbackId && nonce) {
        try {
          await adoptResponderMutation.mutateAsync({
            feedbackId,
            nonce,
          })
        } catch (error) {
          handleOtpError({
            error,
            source: 'verify',
            contactType: contactMethod.type,
            showToUser: false,
            kind: 'verify',
          })
        }
      }

      posthog.identify(user.id, {
        email: user.email || contact.email || user.user_metadata.email || null,
        name: userProfileData.name ?? t(($) => $.common.anonymousParenthesis),
        phoneNumber: user.phone || contact.phone || user.user_metadata.phone_number || null,
        role: 'buyer',
      })

      await onCompleteFlow()
    } catch (error) {
      setFlowIsLoading(false)
      handleOtpError({
        error,
        source: 'verify',
        contactType: contactMethod ? contactMethod.type : 'email',
        showToUser: true,
        kind: 'verify',
      })
    }
  }, [
    adoptResponderMutation,
    contact,
    contactMethod,
    ensureBuyerSetup,
    feedbackId,
    handleOtpError,
    nonce,
    onCompleteFlow,
    otp,
    otpMode,
    posthog,
    resolveOtpMode,
    setFlowIsLoading,
    verifyOtpMutation,
    t,
  ])

  const handleVerifyOtpRef = useRef(handleVerifyOtp)

  useEffect(() => {
    handleVerifyOtpRef.current = handleVerifyOtp
  }, [handleVerifyOtp])

  const setOtp = useCallback((otpValue: string) => {
    _setOtp(otpValue)

    if (otpValue.length !== 6) {
      return
    }

    if (autoSubmitTimeoutRef.current) {
      clearTimeout(autoSubmitTimeoutRef.current)
    }

    autoSubmitTimeoutRef.current = setTimeout(() => {
      void handleVerifyOtpRef.current()
    }, 500)
  }, [])

  const handleResendOtp = useCallback(async () => {
    if (!contactMethod || !otpStateManager.canResendOtp(contactMethod.value)) {
      return
    }

    try {
      const isLoginMode = otpMode === 'login'

      if (isLoginMode) {
        const sendProps =
          contactMethod.type === 'email'
            ? { email: contactMethod.value }
            : { phone: contactMethod.value }

        await sendOtpLogin.mutateAsync(sendProps)
        otpStateManager.markOtpSent(contactMethod.value)
      } else {
        const resendProps =
          contactMethod.type === 'email'
            ? { email: contactMethod.value, type: 'email_change' as const }
            : { phone: contactMethod.value, type: 'phone_change' as const }

        const result = await resendOtpMutation.mutateAsync(resendProps)

        if (result) {
          otpStateManager.markOtpSent(contactMethod.value)
        }
      }
    } catch (error) {
      handleOtpError({
        error,
        source: 'resend',
        contactType: contactMethod.type,
        showToUser: true,
        kind: 'send',
      })
    }
  }, [contactMethod, handleOtpError, otpMode, otpStateManager, resendOtpMutation, sendOtpLogin])

  const hasRecentOtp = !!contactMethod && otpStateManager.hasRecentOtp(contactMethod.value)
  const showOtpPhase = !!contactMethod && (otpSent || hasRecentOtp)

  return {
    contactMethod,
    otp,
    setOtp,
    showOtpPhase,
    isLoading,
    isVerifying,
    sendOtp,
    handleResendOtp,
    otpStateManager,
  }
}
